import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search, Loader2, X } from 'lucide-react';

export interface SearchableSelectOption {
  value: string;
  label: string;
}

interface BaseProps {
  options: SearchableSelectOption[];
  isLoading?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onSearch?: (query: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  error?: string;
  disabled?: boolean;
  emptyText?: string;
  className?: string;
}

interface SingleProps extends BaseProps {
  multiple?: false;
  value?: string;
  onChange?: (value: string) => void;
}

interface MultiProps extends BaseProps {
  multiple: true;
  value?: string[];
  onChange?: (value: string[]) => void;
}

type SearchableSelectProps = SingleProps | MultiProps;

export const SearchableSelect = (props: SearchableSelectProps) => {
  const {
    options,
    isLoading = false,
    hasMore = false,
    onLoadMore,
    onSearch,
    placeholder = 'Select...',
    searchPlaceholder = 'Search...',
    error,
    disabled = false,
    emptyText = 'No results',
    className,
    multiple = false,
  } = props;

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  const selectedValues: string[] = multiple
    ? (props as MultiProps).value ?? []
    : (props as SingleProps).value ? [(props as SingleProps).value!] : [];

  // Labels persist across option-list changes — selection is sourced from
  // `selectedValues`, not filtered out of the current `options` page.
  const selectedLabels = selectedValues.map((v) => {
    const opt = options.find((o) => o.value === v);
    return opt ?? { value: v, label: v };
  });

  const displayText = multiple
    ? selectedLabels.length > 0
      ? `${selectedLabels.length} selected`
      : null
    : selectedLabels[0]?.label ?? null;

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;
    const margin = 8;
    const dropdownMaxH = 320;

    const minWidth = 240;
    const desiredWidth = Math.max(rect.width, minWidth);
    const maxWidth = viewportW - margin * 2;
    const width = Math.min(desiredWidth, maxWidth);

    let left = rect.left;
    if (left + width > viewportW - margin) left = viewportW - width - margin;
    if (left < margin) left = margin;

    const spaceBelow = viewportH - rect.bottom;
    const above = spaceBelow < dropdownMaxH && rect.top > spaceBelow;

    setDropdownStyle({
      position: 'fixed',
      ...(above ? { bottom: viewportH - rect.top + 4 } : { top: rect.bottom + 4 }),
      left,
      width,
      zIndex: 10001,
    });
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        triggerRef.current?.contains(e.target as Node) ||
        dropdownRef.current?.contains(e.target as Node)
      )
        return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (open) {
      updatePosition();
      setTimeout(() => searchRef.current?.focus(), 50);
    } else {
      setSearch('');
      onSearch?.('');
    }
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, updatePosition]);

  const handleListScroll = useCallback(() => {
    const el = listRef.current;
    if (!el || !hasMore || isLoading) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 60) {
      onLoadMore?.();
    }
  }, [hasMore, isLoading, onLoadMore]);

  const handleSearchChange = (val: string) => {
    setSearch(val);
    onSearch?.(val);
  };

  const handleSelect = (val: string) => {
    if (multiple) {
      const current = (props as MultiProps).value ?? [];
      const next = current.includes(val)
        ? current.filter((v) => v !== val)
        : [...current, val];
      (props as MultiProps).onChange?.(next);
    } else {
      (props as SingleProps).onChange?.(val);
      setOpen(false);
    }
  };

  const handleRemoveTag = (val: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!multiple) return;
    const current = (props as MultiProps).value ?? [];
    (props as MultiProps).onChange?.(current.filter((v) => v !== val));
  };

  const dropdown = open ? (
    <div
      ref={dropdownRef}
      style={dropdownStyle}
      className="bg-background border border-foreground/20 rounded-lg shadow-lg overflow-hidden"
    >
      <div className="flex items-center gap-2 px-3 py-1.5 border-b border-foreground/10">
        <Search className="w-3 h-3 text-muted-foreground shrink-0" />
        <input
          ref={searchRef}
          type="text"
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          placeholder={searchPlaceholder}
          className="flex-1 text-xs bg-transparent outline-none placeholder:text-muted-foreground"
        />
        {isLoading && (
          <Loader2 className="w-3 h-3 text-muted-foreground animate-spin shrink-0" />
        )}
      </div>

      <ul
        ref={listRef}
        onScroll={handleListScroll}
        className={`max-h-64 overflow-y-auto ${options.length === 0 && !isLoading ? '' : 'py-1'}`}
      >
        {options.length === 0 && !isLoading && (
          <li className="px-3 py-1.5 text-xs text-muted-foreground">{emptyText}</li>
        )}
        {options.length === 0 && isLoading && (
          <li className="flex justify-center py-2.5">
            <Loader2 className="w-3.5 h-3.5 text-muted-foreground animate-spin" />
          </li>
        )}
        {options.map((opt) => {
          const isSelected = selectedValues.includes(opt.value);
          return (
            <li key={opt.value}>
              <button
                type="button"
                onClick={() => handleSelect(opt.value)}
                className={`w-full text-left px-3 py-1.5 text-xs hover:bg-muted/60 transition-colors truncate flex items-center gap-2 ${
                  isSelected
                    ? 'text-primaryfortext font-medium bg-muted/30'
                    : 'text-foreground'
                }`}
              >
                {multiple && (
                  <span
                    className={`w-3.5 h-3.5 rounded border shrink-0 flex items-center justify-center text-[9px] ${
                      isSelected
                        ? 'bg-primary border-primary text-white'
                        : 'border-foreground/30'
                    }`}
                  >
                    {isSelected && '✓'}
                  </span>
                )}
                {opt.label}
              </button>
            </li>
          );
        })}
        {isLoading && options.length > 0 && (
          <li className="flex justify-center py-1.5">
            <Loader2 className="w-3.5 h-3.5 text-muted-foreground animate-spin" />
          </li>
        )}
      </ul>
    </div>
  ) : null;

  return (
    <div className={className}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 text-sm border rounded-md bg-background shadow-sm transition-colors text-left h-9 ${
          error
            ? 'border-destructive focus-visible:ring-destructive'
            : 'border-foreground/30 hover:bg-muted/50'
        } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
      >
        <span className={`truncate flex-1 ${displayText ? 'text-foreground' : 'text-muted-foreground'}`}>
          {displayText || placeholder}
        </span>
        <ChevronDown
          className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Multi-select tags */}
      {multiple && selectedLabels.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {selectedLabels.map((opt) => (
            <span
              key={opt.value}
              className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-xs text-foreground"
            >
              {opt.label}
              <button
                type="button"
                onClick={(e) => handleRemoveTag(opt.value, e)}
                className="hover:text-destructive"
                aria-label={`Remove ${opt.label}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {error && <p className="mt-1 text-sm text-destructive">{error}</p>}
      {typeof document !== 'undefined' && createPortal(dropdown, document.body)}
    </div>
  );
};
